import React from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, GripVertical, Edit2, Check, X } from 'lucide-react';
import { Button } from '../common/Button';
import { Card } from '../common/Card';

interface AttributeDefinition {
  type: string;
  values?: string[];
  required: boolean;
  prompt_hint?: string;
  max_length?: number;
  // Dynamic enum fields
  source?: string;
  source_field?: string;
  source_filters?: Record<string, any>;
}

interface RichAttributeDefinition {
  key: string;
  type: string;
  prompt_hint?: string;
  values?: string[];
}

interface SectionDefinition {
  description?: string;
  attributes: Record<string, AttributeDefinition>;
  attributes_rich?: RichAttributeDefinition[];
}

interface Schema {
  version: string;
  sections: Record<string, SectionDefinition>;
}

interface SchemaBuilderProps {
  schema: Schema | null;
  onChange: (schema: Schema) => void;
}

const DEFAULT_SCHEMA: Schema = {
  version: '1.0.0',
  sections: {},
};

export const SchemaBuilder: React.FC<SchemaBuilderProps> = ({ schema, onChange }) => {
  const currentSchema = schema || DEFAULT_SCHEMA;

  const addSection = (sectionName: string) => {
    const newSchema = { ...currentSchema };
    if (!newSchema.sections[sectionName]) {
      newSchema.sections[sectionName] = {
        description: '',
        attributes: {},
      };
      onChange(newSchema);
    }
  };

  const removeSection = (sectionName: string) => {
    const newSchema = { ...currentSchema };
    delete newSchema.sections[sectionName];
    onChange(newSchema);
  };

  const updateSectionDescription = (sectionName: string, description: string) => {
    const newSchema = { ...currentSchema };
    newSchema.sections[sectionName].description = description;
    onChange(newSchema);
  };

  const addAttribute = (sectionName: string, attributeName: string) => {
    const newSchema = { ...currentSchema };
    if (!newSchema.sections[sectionName].attributes[attributeName]) {
      newSchema.sections[sectionName].attributes[attributeName] = {
        type: 'string',
        required: false,
      };
      onChange(newSchema);
    }
  };

  const removeAttribute = (sectionName: string, attributeName: string) => {
    const newSchema = { ...currentSchema };
    delete newSchema.sections[sectionName].attributes[attributeName];
    onChange(newSchema);
  };

  const renameAttribute = (sectionName: string, oldName: string, newName: string) => {
    if (oldName === newName || !newName.trim()) return;

    const newSchema = { ...currentSchema };
    const section = newSchema.sections[sectionName];

    // Check if new name already exists
    if (section.attributes[newName]) {
      alert(`Attribute "${newName}" already exists in this section`);
      return;
    }

    // Copy attribute with new name
    section.attributes[newName] = { ...section.attributes[oldName] };
    delete section.attributes[oldName];

    onChange(newSchema);
  };

  const updateAttribute = (
    sectionName: string,
    attributeName: string,
    field: keyof AttributeDefinition,
    value: any
  ) => {
    const newSchema = { ...currentSchema };
    const attr = newSchema.sections[sectionName].attributes[attributeName];
    (attr as any)[field] = value;
    onChange(newSchema);
  };

  const addEnumValue = (sectionName: string, attributeName: string, value: string) => {
    const newSchema = { ...currentSchema };
    const attr = newSchema.sections[sectionName].attributes[attributeName];
    if (!attr.values) {
      attr.values = [];
    }
    attr.values.push(value);
    onChange(newSchema);
  };

  const removeEnumValue = (sectionName: string, attributeName: string, index: number) => {
    const newSchema = { ...currentSchema };
    const attr = newSchema.sections[sectionName].attributes[attributeName];
    if (attr.values) {
      attr.values.splice(index, 1);
    }
    onChange(newSchema);
  };

  const addRichAttribute = (sectionName: string, richAttr: RichAttributeDefinition) => {
    const newSchema = { ...currentSchema };
    const section = newSchema.sections[sectionName];
    if (!section.attributes_rich) {
      section.attributes_rich = [];
    }
    section.attributes_rich.push(richAttr);
    onChange(newSchema);
  };

  const removeRichAttribute = (sectionName: string, index: number) => {
    const newSchema = { ...currentSchema };
    const section = newSchema.sections[sectionName];
    if (section.attributes_rich) {
      section.attributes_rich.splice(index, 1);
    }
    onChange(newSchema);
  };

  const updateRichAttribute = (
    sectionName: string,
    index: number,
    field: keyof RichAttributeDefinition,
    value: any
  ) => {
    const newSchema = { ...currentSchema };
    const section = newSchema.sections[sectionName];
    if (section.attributes_rich && section.attributes_rich[index]) {
      (section.attributes_rich[index] as any)[field] = value;
    }
    onChange(newSchema);
  };

  const addRichEnumValue = (sectionName: string, index: number, value: string) => {
    const newSchema = { ...currentSchema };
    const section = newSchema.sections[sectionName];
    if (section.attributes_rich && section.attributes_rich[index]) {
      const richAttr = section.attributes_rich[index];
      if (!richAttr.values) {
        richAttr.values = [];
      }
      richAttr.values.push(value);
    }
    onChange(newSchema);
  };

  const removeRichEnumValue = (sectionName: string, index: number, valueIndex: number) => {
    const newSchema = { ...currentSchema };
    const section = newSchema.sections[sectionName];
    if (section.attributes_rich && section.attributes_rich[index] && section.attributes_rich[index].values) {
      section.attributes_rich[index].values!.splice(valueIndex, 1);
    }
    onChange(newSchema);
  };

  const [newSectionName, setNewSectionName] = React.useState('');
  const [showCustomSectionInput, setShowCustomSectionInput] = React.useState(false);

  const handleAddCustomSection = () => {
    if (newSectionName.trim()) {
      addSection(newSectionName.trim());
      setNewSectionName('');
      setShowCustomSectionInput(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Section Buttons */}
      <div className="space-y-3">
        <div className="flex gap-3 flex-wrap">
          <Button
            type="button"
            onClick={() => addSection('square_projection')}
            variant="outline"
            icon={Plus}
            disabled={!!currentSchema.sections.square_projection}
          >
            Add square_projection Section
          </Button>
          <Button
            type="button"
            onClick={() => addSection('internal_record')}
            variant="outline"
            icon={Plus}
            disabled={!!currentSchema.sections.internal_record}
          >
            Add internal_record Section
          </Button>
          <Button
            type="button"
            onClick={() => setShowCustomSectionInput(!showCustomSectionInput)}
            variant="outline"
            icon={Plus}
            className="border-purple-300 text-purple-600 hover:bg-purple-50"
          >
            Add Custom Section
          </Button>
        </div>

        {/* Custom Section Input */}
        {showCustomSectionInput && (
          <div className="flex gap-2 items-center p-3 bg-purple-50 border border-purple-200 rounded">
            <input
              type="text"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAddCustomSection();
                if (e.key === 'Escape') {
                  setShowCustomSectionInput(false);
                  setNewSectionName('');
                }
              }}
              placeholder="Section name (e.g., shipping_info)"
              className="flex-1 px-3 py-2 border border-purple-300 rounded text-sm font-mono focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
            <button
              type="button"
              onClick={handleAddCustomSection}
              className="p-2 text-green-600 hover:bg-green-50 rounded"
              title="Add section"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCustomSectionInput(false);
                setNewSectionName('');
              }}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Render Sections */}
      {Object.entries(currentSchema.sections).map(([sectionName, sectionDef]) => (
        <SectionEditor
          key={sectionName}
          sectionName={sectionName}
          section={sectionDef}
          onRemove={() => removeSection(sectionName)}
          onUpdateDescription={(desc) => updateSectionDescription(sectionName, desc)}
          onAddAttribute={(name) => addAttribute(sectionName, name)}
          onRemoveAttribute={(name) => removeAttribute(sectionName, name)}
          onRenameAttribute={(oldName, newName) => renameAttribute(sectionName, oldName, newName)}
          onUpdateAttribute={(name, field, value) => updateAttribute(sectionName, name, field, value)}
          onAddEnumValue={(name, value) => addEnumValue(sectionName, name, value)}
          onRemoveEnumValue={(name, index) => removeEnumValue(sectionName, name, index)}
          onAddRichAttribute={(richAttr) => addRichAttribute(sectionName, richAttr)}
          onRemoveRichAttribute={(index) => removeRichAttribute(sectionName, index)}
          onUpdateRichAttribute={(index, field, value) => updateRichAttribute(sectionName, index, field, value)}
          onAddRichEnumValue={(index, value) => addRichEnumValue(sectionName, index, value)}
          onRemoveRichEnumValue={(index, valueIndex) => removeRichEnumValue(sectionName, index, valueIndex)}
        />
      ))}

      {Object.keys(currentSchema.sections).length === 0 && (
        <div className="p-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-center">
          <p className="text-gray-600">
            No sections defined yet. Click "Add Section" above to get started.
          </p>
        </div>
      )}
    </div>
  );
};

interface SectionEditorProps {
  sectionName: string;
  section: SectionDefinition;
  onRemove: () => void;
  onUpdateDescription: (description: string) => void;
  onAddAttribute: (attributeName: string) => void;
  onRemoveAttribute: (attributeName: string) => void;
  onRenameAttribute: (oldName: string, newName: string) => void;
  onUpdateAttribute: (attributeName: string, field: keyof AttributeDefinition, value: any) => void;
  onAddEnumValue: (attributeName: string, value: string) => void;
  onRemoveEnumValue: (attributeName: string, index: number) => void;
  onAddRichAttribute: (richAttr: RichAttributeDefinition) => void;
  onRemoveRichAttribute: (index: number) => void;
  onUpdateRichAttribute: (index: number, field: keyof RichAttributeDefinition, value: any) => void;
  onAddRichEnumValue: (index: number, value: string) => void;
  onRemoveRichEnumValue: (index: number, valueIndex: number) => void;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  sectionName,
  section,
  onRemove,
  onUpdateDescription,
  onAddAttribute,
  onRemoveAttribute,
  onRenameAttribute,
  onUpdateAttribute,
  onAddEnumValue,
  onRemoveEnumValue,
  onAddRichAttribute,
  onRemoveRichAttribute,
  onUpdateRichAttribute,
  onAddRichEnumValue,
  onRemoveRichEnumValue,
}) => {
  const [newAttributeName, setNewAttributeName] = React.useState('');
  const [expanded, setExpanded] = React.useState(true);
  const [showRichAttributes, setShowRichAttributes] = React.useState(true);

  const handleAddAttribute = () => {
    if (newAttributeName.trim()) {
      onAddAttribute(newAttributeName.trim());
      setNewAttributeName('');
    }
  };

  const handleAddRichAttribute = () => {
    onAddRichAttribute({
      key: '',
      type: 'string',
      prompt_hint: '',
    });
  };

  return (
    <Card className="border-2 border-purple-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-gray-400 hover:text-gray-600"
            >
              {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            <h3 className="text-lg font-bold text-purple-900">{sectionName}</h3>
          </div>
          <input
            type="text"
            value={section.description || ''}
            onChange={(e) => onUpdateDescription(e.target.value)}
            placeholder="Section description..."
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
          />
        </div>
        <Button
          type="button"
          onClick={onRemove}
          variant="ghost"
          size="sm"
          icon={Trash2}
          className="text-red-600 hover:bg-red-50"
        >
          Remove Section
        </Button>
      </div>

      {expanded && (
        <div className="space-y-4">
          {/* Add Attribute Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newAttributeName}
              onChange={(e) => setNewAttributeName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
              placeholder="New attribute name (e.g., 'audience', 'color')"
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <Button
              type="button"
              onClick={handleAddAttribute}
              variant="outline"
              size="sm"
              icon={Plus}
            >
              Add Attribute
            </Button>
          </div>

          {/* Attributes List */}
          <div className="space-y-3">
            {Object.entries(section.attributes).map(([attrName, attrDef]) => (
              <AttributeEditor
                key={attrName}
                attributeName={attrName}
                attribute={attrDef}
                onRemove={() => onRemoveAttribute(attrName)}
                onRename={(newName) => onRenameAttribute(attrName, newName)}
                onUpdate={(field, value) => onUpdateAttribute(attrName, field, value)}
                onAddEnumValue={(value) => onAddEnumValue(attrName, value)}
                onRemoveEnumValue={(index) => onRemoveEnumValue(attrName, index)}
              />
            ))}
          </div>

          {Object.keys(section.attributes).length === 0 && (
            <p className="text-sm text-gray-500 italic">
              No attributes yet. Add an attribute using the input above.
            </p>
          )}

          {/* Attributes Rich Section */}
          <div className="mt-6 pt-6 border-t-2 border-gray-300">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowRichAttributes(!showRichAttributes)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showRichAttributes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <h4 className="text-md font-semibold text-gray-800">
                  attributes_rich
                </h4>
                <span className="text-xs text-gray-500 italic">
                  (Advanced: Array of key-value pairs)
                </span>
              </div>
              <Button
                type="button"
                onClick={handleAddRichAttribute}
                variant="outline"
                size="sm"
                icon={Plus}
              >
                Add Rich Attribute
              </Button>
            </div>

            {showRichAttributes && (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                  <strong>What is attributes_rich?</strong> This creates an array of key-value objects in the AI response,
                  instead of top-level attributes. Example: <code className="bg-blue-100 px-1 rounded">attributes_rich: [{"{"}key: "motifs", value: "cherry blossoms"{"}"}]</code>
                </div>

                {section.attributes_rich && section.attributes_rich.length > 0 ? (
                  section.attributes_rich.map((richAttr, index) => (
                    <RichAttributeEditor
                      key={index}
                      index={index}
                      richAttribute={richAttr}
                      onRemove={() => onRemoveRichAttribute(index)}
                      onUpdate={(field, value) => onUpdateRichAttribute(index, field, value)}
                      onAddEnumValue={(value) => onAddRichEnumValue(index, value)}
                      onRemoveEnumValue={(valueIndex) => onRemoveRichEnumValue(index, valueIndex)}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No rich attributes yet. Click "Add Rich Attribute" to add one.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

interface AttributeEditorProps {
  attributeName: string;
  attribute: AttributeDefinition;
  onRemove: () => void;
  onRename: (newName: string) => void;
  onUpdate: (field: keyof AttributeDefinition, value: any) => void;
  onAddEnumValue: (value: string) => void;
  onRemoveEnumValue: (index: number) => void;
}

const AttributeEditor: React.FC<AttributeEditorProps> = ({
  attributeName,
  attribute,
  onRemove,
  onRename,
  onUpdate,
  onAddEnumValue,
  onRemoveEnumValue,
}) => {
  const [newEnumValue, setNewEnumValue] = React.useState('');
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [editedName, setEditedName] = React.useState(attributeName);

  const handleAddEnumValue = () => {
    if (newEnumValue.trim()) {
      onAddEnumValue(newEnumValue.trim());
      setNewEnumValue('');
    }
  };

  const handleSaveRename = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== attributeName) {
      onRename(trimmedName);
    }
    setIsEditingName(false);
  };

  const handleCancelRename = () => {
    setEditedName(attributeName);
    setIsEditingName(false);
  };

  // Update editedName when attributeName changes (after successful rename)
  React.useEffect(() => {
    setEditedName(attributeName);
  }, [attributeName]);

  return (
    <div className="p-4 bg-gray-50 border border-gray-200 rounded">
      <div className="flex items-start gap-3">
        <GripVertical className="h-5 w-5 text-gray-400 mt-2 flex-shrink-0" />

        <div className="flex-1 space-y-3">
          {/* Attribute Name & Type */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Attribute Name
              </label>
              {isEditingName ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleSaveRename();
                      if (e.key === 'Escape') handleCancelRename();
                    }}
                    className="flex-1 px-3 py-2 border border-purple-300 rounded text-sm font-mono focus:ring-2 focus:ring-purple-500"
                    aria-label="Edit attribute name"
                    placeholder="Attribute name"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleSaveRename}
                    className="p-2 text-green-600 hover:bg-green-50 rounded"
                    title="Save"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelRename}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                    title="Cancel"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm font-mono">
                    {attributeName}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsEditingName(true)}
                    className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                    title="Rename attribute"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Type
              </label>
              <select
                value={attribute.type}
                onChange={(e) => onUpdate('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="enum">Enum (Select from values)</option>
                <option value="enum_dynamic">Enum Dynamic (From data source)</option>
                <option value="color">Color</option>
                <option value="color_or_null">Color or Null</option>
                <option value="comma_separated">Comma Separated List</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  checked={attribute.required}
                  onChange={(e) => onUpdate('required', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium">Required</span>
              </label>
            </div>
          </div>

          {/* Prompt Hint */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Prompt Hint (instructions for AI)
            </label>
            <input
              type="text"
              value={attribute.prompt_hint || ''}
              onChange={(e) => onUpdate('prompt_hint', e.target.value)}
              placeholder="e.g., 'Target audience based on sizing and style'"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Enum Values */}
          {attribute.type === 'enum' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Allowed Values
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newEnumValue}
                  onChange={(e) => setNewEnumValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEnumValue()}
                  placeholder="Add value (e.g., 'Women', 'Men', 'Unisex')"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <Button
                  type="button"
                  onClick={handleAddEnumValue}
                  variant="outline"
                  size="sm"
                  icon={Plus}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {attribute.values?.map((value, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1 bg-white border border-purple-200 rounded-full text-sm"
                  >
                    <span>{value}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveEnumValue(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              {(!attribute.values || attribute.values.length === 0) && (
                <p className="text-xs text-gray-500 italic">No values yet. Add values above.</p>
              )}
            </div>
          )}

          {/* Dynamic Enum Source */}
          {attribute.type === 'enum_dynamic' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Data Source
                </label>
                <select
                  value={attribute.source || ''}
                  onChange={(e) => onUpdate('source', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  aria-label="Dynamic enum data source"
                >
                  <option value="">-- Select Source --</option>
                  <option value="square_categories">Square Categories</option>
                  <option value="square_variations">Square Variations (Future)</option>
                  <option value="users">Organization Users (Future)</option>
                  <option value="organizations">Organizations (Future)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Values will be fetched automatically from the selected data source
                </p>
              </div>

              {attribute.source === 'square_categories' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Square Categories:</strong> Categories created in your Square catalog will automatically appear as options.
                  </p>
                </div>
              )}

              {attribute.source && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Source Field (optional)
                  </label>
                  <input
                    type="text"
                    value={attribute.source_field || 'name'}
                    onChange={(e) => onUpdate('source_field', e.target.value)}
                    placeholder="Field to use for values (default: name)"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Which field from the source to use (e.g., "name", "id", "email")
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Max Length */}
          {attribute.type === 'string' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Max Length (optional)
              </label>
              <input
                type="number"
                value={attribute.max_length || ''}
                onChange={(e) => onUpdate('max_length', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 5000"
                className="w-32 px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={onRemove}
          variant="ghost"
          size="sm"
          icon={Trash2}
          className="text-red-600 hover:bg-red-50"
        >
          Remove
        </Button>
      </div>
    </div>
  );
};

interface RichAttributeEditorProps {
  index: number;
  richAttribute: RichAttributeDefinition;
  onRemove: () => void;
  onUpdate: (field: keyof RichAttributeDefinition, value: any) => void;
  onAddEnumValue: (value: string) => void;
  onRemoveEnumValue: (index: number) => void;
}

const RichAttributeEditor: React.FC<RichAttributeEditorProps> = ({
  index,
  richAttribute,
  onRemove,
  onUpdate,
  onAddEnumValue,
  onRemoveEnumValue,
}) => {
  const [newEnumValue, setNewEnumValue] = React.useState('');

  const handleAddEnumValue = () => {
    if (newEnumValue.trim()) {
      onAddEnumValue(newEnumValue.trim());
      setNewEnumValue('');
    }
  };

  return (
    <div className="p-4 bg-amber-50 border border-amber-200 rounded">
      <div className="flex items-start gap-3">
        <GripVertical className="h-5 w-5 text-amber-400 mt-2 flex-shrink-0" />

        <div className="flex-1 space-y-3">
          {/* Key & Type */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Key
              </label>
              <input
                type="text"
                value={richAttribute.key}
                onChange={(e) => onUpdate('key', e.target.value)}
                placeholder="e.g., 'motifs', 'construction'"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
              />
            </div>

            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Type
              </label>
              <select
                value={richAttribute.type}
                onChange={(e) => onUpdate('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                aria-label="Rich attribute type"
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="enum">Enum (Select from values)</option>
                <option value="comma_separated">Comma Separated List</option>
              </select>
            </div>
          </div>

          {/* Prompt Hint */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Prompt Hint (instructions for AI)
            </label>
            <input
              type="text"
              value={richAttribute.prompt_hint || ''}
              onChange={(e) => onUpdate('prompt_hint', e.target.value)}
              placeholder="e.g., 'Visual motifs and patterns'"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Enum Values */}
          {richAttribute.type === 'enum' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Allowed Values
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newEnumValue}
                  onChange={(e) => setNewEnumValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEnumValue()}
                  placeholder="Add value"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <Button
                  type="button"
                  onClick={handleAddEnumValue}
                  variant="outline"
                  size="sm"
                  icon={Plus}
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {richAttribute.values?.map((value, valueIndex) => (
                  <div
                    key={valueIndex}
                    className="flex items-center gap-2 px-3 py-1 bg-white border border-amber-300 rounded-full text-sm"
                  >
                    <span>{value}</span>
                    <button
                      type="button"
                      onClick={() => onRemoveEnumValue(valueIndex)}
                      className="text-red-600 hover:text-red-800"
                      title={`Remove ${value}`}
                      aria-label={`Remove ${value}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              {(!richAttribute.values || richAttribute.values.length === 0) && (
                <p className="text-xs text-gray-500 italic">No values yet. Add values above.</p>
              )}
            </div>
          )}
        </div>

        <Button
          type="button"
          onClick={onRemove}
          variant="ghost"
          size="sm"
          icon={Trash2}
          className="text-red-600 hover:bg-red-50"
        >
          Remove
        </Button>
      </div>
    </div>
  );
};
